import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import ImageWithFallback from "../components/ImageWithFallback";
import { countdownLabel } from "../lib/countdown";
import { Sparkle, Check, Trash2, Clock } from "lucide-react";

export default function Trips() {
    const [trips, setTrips] = useState(null);
    const [error, setError] = useState("");
    const load = useCallback(() => {
        return api.get("/trips")
            .then((r) => setTrips(r.data.trips))
            .catch((e) => setError(e.response?.data?.detail || e.message));
    }, []);
    useEffect(() => { load(); }, [load]);

    const toggleVisited = async (t) => {
        await api.patch(`/trips/${t.trip_id}/visit`, { visited: !t.visited });
        load();
    };
    const del = async (t) => {
        if (!window.confirm("Remove this trip?")) return;
        await api.delete(`/trips/${t.trip_id}`);
        load();
    };

    return (
        <Layout>
            <div className="sp-container pt-12 pb-24">
                <div className="sp-overline">Saved trips</div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif mt-3 leading-[1.05]">Your celestial archive.</h1>

                {error && <div className="mt-6 text-red-700 text-sm" data-testid="trips-error">{error}</div>}

                {trips !== null && trips.length === 0 && (
                    <div className="mt-12 bg-surface border border-ink/10 rounded-2xl p-10 text-center" data-testid="trips-empty">
                        <div className="sp-overline">No trips yet</div>
                        <p className="mt-3 text-ink-soft">Save a recommendation to begin your archive.</p>
                        <Link to="/dashboard" className="sp-btn-primary mt-6 inline-flex">See recommendations</Link>
                    </div>
                )}

                {trips && trips.length > 0 && (
                    <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="trips-list">
                        {trips.map((t) => {
                            const r = t.snapshot;
                            const countdown = !t.visited ? countdownLabel(r.window) : null;
                            return (
                                <div key={t.trip_id} className="sp-card" data-testid={`trip-card-${t.trip_id}`}>
                                    <div className="relative aspect-[4/3] overflow-hidden">
                                        <ImageWithFallback src={r.image} alt={r.city} accentColor={r.planet_color} imgClassName="w-full h-full object-cover" />
                                        <div className="absolute top-4 left-4 sp-pill"><Sparkle strokeWidth={1.25} className="w-3 h-3" style={{ color: r.planet_color }} /> {r.planet}</div>
                                        {t.visited && (
                                            <div className="absolute top-4 right-4 sp-pill bg-ink text-sand-50 border-ink" data-testid={`trip-card-${t.trip_id}-visited-badge`}>
                                                <Check strokeWidth={1.5} className="w-3 h-3" /> Visited
                                            </div>
                                        )}
                                        {countdown && (
                                            <div
                                                className={`absolute bottom-4 left-4 sp-pill ${countdown.urgent ? "bg-ink text-sand-50 border-ink" : ""}`}
                                                data-testid={`trip-card-${t.trip_id}-countdown`}
                                            >
                                                <Clock strokeWidth={1.5} className="w-3 h-3" />
                                                {countdown.text}
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-6">
                                        <div className="sp-overline">{r.planet_theme}</div>
                                        <h3 className="text-2xl font-serif mt-2 leading-tight">{r.city}</h3>
                                        <div className="text-sm text-ink-muted">{r.country}</div>
                                        <div className="mt-4 flex items-center gap-2">
                                            <Link to={`/recommendation/${r.id}?season=${t.season}`} className="text-sm sp-link" data-testid={`trip-card-${t.trip_id}-open`}>Open</Link>
                                            <button onClick={() => toggleVisited(t)} className="ml-auto text-xs text-ink-muted hover:text-ink" data-testid={`trip-card-${t.trip_id}-toggle-visited`}>
                                                {t.visited ? "Mark unvisited" : "Mark as visited"}
                                            </button>
                                            <button onClick={() => del(t)} className="text-ink-muted hover:text-red-700" aria-label="Delete" data-testid={`trip-card-${t.trip_id}-delete`}>
                                                <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </Layout>
    );
}
